/**
 * ENG-8623 — prove Claude Code can actually authenticate, instead of inferring
 * it from a file.
 *
 * ## Why this module exists
 *
 * `hosts.agent_runtime_authenticated` is named for a runtime observation but was
 * never one. It came from `checkClaudeAuth()`, a thin wrapper over
 * `detectClaudeAuth()` — the same credentials-FILE reader that produces
 * `claude_auth_status`. `computeSubscriptionStatus()` pins the status to `valid`
 * whenever a `refreshToken` key is present, *regardless of expiry*, and says so
 * in its own comment:
 *
 *   > we don't track refresh-token expiry because Claude Code's credentials
 *   > format doesn't expose one … If one does expire, Claude Code surfaces the
 *   > failure at the next auto-refresh attempt, which the operator will see in
 *   > their manager.log.
 *
 * So in the exact failure this ticket is about — a refresh token that has itself
 * died — every file-derived signal reads healthy while no agent on the host can
 * start. The ground truth exists, in a log nobody reads.
 *
 * ## Why the round-trip, and not something cheaper
 *
 * Measured on 2026-08-09 against a synthetic dead credential (a fabricated
 * `refreshToken` with a 2025 `expiresAt`), so these are observations, not
 * expectations:
 *
 *   | probe                       | dead credential          | live credential |
 *   |-----------------------------|--------------------------|-----------------|
 *   | reading the file ourselves  | `valid`                  | `valid`         |
 *   | `claude auth status --json` | `{"loggedIn": true …}`   | `loggedIn:true` |
 *   | `claude -p <sentinel>`      | exit 1, "Failed to       | exit 0, prints  |
 *   |                             | authenticate: OAuth      | the sentinel    |
 *   |                             | session expired and      |                 |
 *   |                             | could not be refreshed"  |                 |
 *
 * `claude auth status` looked like the cheap answer and is not one: it reports
 * `loggedIn: true` for a credential that cannot authenticate at all. Only a real
 * round-trip separates them, so that is what this does — one Haiku call per
 * probe interval, which is nothing next to a host-wide outage nobody can see.
 *
 * ## The classification is deliberately asymmetric
 *
 * A wrong `not-authenticated` pages the whole fleet on a network blip; a wrong
 * `authenticated` is what we already have today. So:
 *
 *   * `authenticated` requires POSITIVE proof — exit 0 AND the sentinel came
 *     back. Anthropic accepted the credential end to end.
 *   * `not-authenticated` requires an explicit auth-shaped refusal.
 *   * EVERYTHING ELSE is `inconclusive`, and inconclusive must never move the
 *     reported value. A transport failure exits 124 with "Execution error" and
 *     matches no marker — verified above. If `claude -p` is ever withdrawn
 *     (ENG-5576 flags it as deprecating under Max) its failure will not match
 *     either, so the probe degrades to inconclusive and the host falls back to
 *     the file-derived answer: today's behaviour, not a fleet-wide false alarm.
 */

/** Sent as the prompt; must come back verbatim for a positive verdict. */
export const RUNTIME_AUTH_SENTINEL = 'AGTAUTHOK';

export const RUNTIME_AUTH_PROBE_PROMPT =
  `Reply with exactly this and nothing else: ${RUNTIME_AUTH_SENTINEL}`;

export type RuntimeAuthVerdict = 'authenticated' | 'not-authenticated' | 'inconclusive';

export type RuntimeAuthProbeResult = {
  verdict: RuntimeAuthVerdict;
  /** Short, non-secret reason. Safe to log and to put in an alert payload. */
  detail: string;
};

/**
 * Markers that mean "Anthropic refused this credential", as emitted by Claude
 * Code itself. Matched case-insensitively against combined stdout+stderr.
 *
 * Kept SHORT and specific on purpose. A broad pattern like /auth/ would match a
 * transport error that merely mentions an auth header, and the cost of a false
 * `not-authenticated` is a fleet-wide page. When in doubt the classifier must
 * fall through to `inconclusive`, so a marker that is missing costs us one probe
 * interval of silence, while a marker that is too greedy costs an incident.
 */
const AUTH_REFUSAL_MARKERS = [
  'failed to authenticate',
  'oauth session expired',
  'could not be refreshed',
  'invalid api key',
  'authentication_error',
  'please run /login',
  'please run `claude /login`',
] as const;

/** Redact anything that could carry credential material out of a probe log. */
function safeDetail(raw: string): string {
  return raw
    .replace(/\bsk-[a-zA-Z0-9-]+/g, 'sk-<redacted>')
    .replace(/\b(Bearer|token|refreshToken|accessToken)\s*[:=]?\s*\S+/gi, '$1 <redacted>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200);
}

/**
 * The whole risk of this feature lives in this function, so it is pure: no
 * spawning, no filesystem, no clock. Everything above it is plumbing.
 */
export function classifyRuntimeAuthProbe(input: {
  exitCode: number | null;
  stdout: string;
  stderr: string;
  /** True when the child was killed by our own timeout. */
  timedOut?: boolean;
}): RuntimeAuthProbeResult {
  const { exitCode, stdout, stderr, timedOut } = input;
  const combined = `${stdout}\n${stderr}`;
  const haystack = combined.toLowerCase();

  // A timeout tells us nothing about the credential. Check it before the
  // markers: a partially-written line could contain anything.
  if (timedOut) {
    return { verdict: 'inconclusive', detail: 'probe timed out' };
  }

  const marker = AUTH_REFUSAL_MARKERS.find((m) => haystack.includes(m));
  if (marker) {
    return {
      verdict: 'not-authenticated',
      detail: safeDetail(combined) || `matched: ${marker}`,
    };
  }

  if (exitCode === 0 && stdout.includes(RUNTIME_AUTH_SENTINEL)) {
    return { verdict: 'authenticated', detail: 'sentinel returned' };
  }

  // Exit 0 without the sentinel is NOT proof. The credential may be fine and the
  // model merely chatty, but this probe's only job is to be certain, and it
  // isn't. Say so rather than guessing in either direction.
  if (exitCode === 0) {
    return { verdict: 'inconclusive', detail: 'exit 0 but sentinel absent' };
  }

  return {
    verdict: 'inconclusive',
    detail: safeDetail(`exit ${exitCode ?? 'null'}: ${combined}`),
  };
}

/**
 * Fold a probe verdict into the boolean the heartbeat reports.
 *
 * `previous` is what we would report without this probe — today's file-derived
 * answer. An `inconclusive` verdict returns it unchanged, so the probe can only
 * ever add information.
 *
 * A `not-authenticated` verdict must be CONFIRMED across
 * `requiredConsecutiveFailures` probes before it flips the boolean. One refusal
 * is already specific, but the blast radius of being wrong is every agent on the
 * host paging, so it is worth one more interval of patience. A single
 * `authenticated` clears the counter immediately — recovery should be believed
 * fast and failure slowly, not the other way round.
 */
export function foldRuntimeAuthVerdict(input: {
  verdict: RuntimeAuthVerdict;
  previous: boolean;
  consecutiveFailures: number;
  requiredConsecutiveFailures: number;
}): { reported: boolean; consecutiveFailures: number } {
  const { verdict, previous, consecutiveFailures, requiredConsecutiveFailures } = input;

  if (verdict === 'authenticated') {
    return { reported: true, consecutiveFailures: 0 };
  }

  if (verdict === 'not-authenticated') {
    const next = consecutiveFailures + 1;
    return {
      reported: next >= requiredConsecutiveFailures ? false : previous,
      consecutiveFailures: next,
    };
  }

  // Inconclusive: carry the previous answer AND the counter. Deliberately not
  // reset — a host flapping between refusal and timeout is not recovering, and
  // resetting on every blip would let a genuinely broken host never reach the
  // threshold.
  return { reported: previous, consecutiveFailures };
}

/**
 * What the heartbeat is allowed to put on the wire for
 * `agent_runtime_authenticated` — the probe's verdict, or NOTHING.
 *
 * `undefined` (send nothing) is the case that matters. The API stamps
 * `agent_runtime_auth_reported_at` for every boolean it receives, and the alert
 * trigger reads a fresh `false` as "the runtime refused a session". So putting
 * the file-derived value on the wire does not merely lose precision — it
 * manufactures an observation nobody made, and it does so on exactly the hosts
 * the probe cannot run on (no `claude` binary, or a probe that has never reached
 * a verdict). The resulting critical would name `[runtime-auth-probe]` as its
 * diagnostic tag and send an operator looking for log lines that do not exist.
 *
 * That is the same defect this ticket is about, one layer down: asserting a
 * check that never happened. A host with no verdict reports nothing, its
 * timestamp goes stale, and the trigger decays it to silence — the same path as
 * a host that stopped reporting. The file-derived lane still covers it, exactly
 * as it did before this feature existed.
 */
export function heartbeatRuntimeAuthReport(
  probeVerdict: boolean | null,
): boolean | undefined {
  return probeVerdict ?? undefined;
}

/**
 * The heartbeat's runtime-auth fields, produced as ONE unit.
 *
 * Omitting the boolean is necessary but NOT sufficient, and that gap shipped to
 * production on 2026-08-09. Every CLI at or below 0.28.540 sends
 * `agent_runtime_authenticated` unconditionally, carrying the file-derived
 * answer, because that is what the field meant before the probe existed. The API
 * cannot tell that apart from a probe verdict by value, so when it began
 * stamping a freshness timestamp on every boolean it received, it manufactured
 * runtime observations for the whole rollout window — `gccm-host` was condemned
 * 8.5 minutes after the probe shipped, by a file-derived value, in an alert that
 * credited a probe that had never run on that host.
 *
 * `agent_runtime_auth_observed` closes it, and the signal is deliberately
 * POSITIVE: an old build cannot accidentally emit it, whereas any scheme based
 * on absence (a version check, a missing field) has to guess. The API stamps
 * only when it is present.
 *
 * They are returned together because the failure mode is them drifting apart. A
 * boolean without the marker is silently ignored by the API; a marker without
 * the boolean stamps a timestamp for nothing. Callers spread this, so there is
 * no call site at which one can be edited without the other.
 */
export function heartbeatRuntimeAuthFields(
  probeVerdict: boolean | null,
):
  | { agent_runtime_authenticated: boolean; agent_runtime_auth_observed: true }
  | Record<string, never> {
  const reported = heartbeatRuntimeAuthReport(probeVerdict);
  if (reported === undefined) return {};
  return {
    agent_runtime_authenticated: reported,
    agent_runtime_auth_observed: true,
  };
}
